<script setup>
import { AXES_NAMES_SUPPORTED_CHARTS, DUAL_Y_AXIS_SUPPORTED_CHARTS, SERIES_SUPPORTED_CHARTS } from '~/bi/constants/bi-constants';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  chartType: {
    type: String,
    required: true,
  },
  chartConfig: {
    type: Object,
    required: true,
  },
});

const { $t } = useCommonImports();

const state = reactive({
  are_more_options_visible: false,
});

const scale_types = [
  ['linear', $t('Linear')],
  ['log', $t('Logarithmic')],
].map((item) => {
  return {
    value: item[0],
    label: item[1],
  };
});

const tick_label_types = [
  ['show', $t('Show')],
  ['rotate45', $t('Rotate 45°')],
  ['rotate90', $t('Rotate 90°')],
  ['hide', $t('Hide')],
].map((item) => {
  return {
    value: item[0],
    label: item[1],
  };
});

const has_axes_names = computed(() => AXES_NAMES_SUPPORTED_CHARTS.includes(props.chartType));
const has_scale_type = computed(() => SERIES_SUPPORTED_CHARTS.includes(props.chartType));
const has_tick_labels = computed(() => [...SERIES_SUPPORTED_CHARTS, 'pareto_chart'].includes(props.chartType));
const has_primary_custom_range = computed(() => SERIES_SUPPORTED_CHARTS.includes(props.chartType));
const has_secondary_y_axis = computed(() => {
  const chart_config = props.chartConfig;
  if (['pareto_chart'].includes(props.chartType))
    return true;
  if (DUAL_Y_AXIS_SUPPORTED_CHARTS.includes(props.chartType) && Array.isArray(chart_config.layout_values)) {
    return chart_config.layout_values.some(item => item.y_axis === 'secondary');
  }
  return false;
});
const primary_scale_label = computed(() => {
  if (DUAL_Y_AXIS_SUPPORTED_CHARTS.includes(props.chartType))
    return $t('Y-axis');
  else if (['horizontalBar_chart'].includes(props.chartType))
    return $t('X-axis');
  return null;
});
const secondary_scale_label = computed(() => {
  if (DUAL_Y_AXIS_SUPPORTED_CHARTS.includes(props.chartType) && has_secondary_y_axis.value)
    return $t('Secondary Y-axis');
  return null;
});

function getAxis(type) {
  // type = 'category' || 'value'
  if (props.chartConfig.type === 'horizontalBar_chart') {
    // For horizontal bar charts, X and Y should be displayed in reverse
    if (type === 'category')
      return 'y';
    else return 'x';
  }
  else {
    if (type === 'category')
      return 'x';
    else return 'y';
  }
}
</script>

<template>
  <template v-if="has_axes_names">
    <div class="text-sm font-semibold text-gray-900 -mb-3">
      {{ $t('Axis names') }}
    </div>
    <div class="flex gap-3">
      <TextElement
        name="category_axis_name"
        :label="getAxis('category') === 'x' ? $t('X-axis') : $t('Y-axis')"
        :placeholder="$t('Enter label')"
        class="w-full"
      />
      <TextElement
        name="value_axis_name"
        :label="getAxis('value') === 'x' ? $t('X-axis') : $t('Y-axis')"
        :placeholder="$t('Enter label')"
        class="w-full"
      />
    </div>
    <TextElement
      v-if="has_secondary_y_axis"
      name="secondary_y_axis"
      :label="$t('Secondary Y-axis name')"
      :placeholder="$t('Enter label')"
      class="w-[calc(50%-6px)] -mt-3"
    />
  </template>
  <div v-if="has_tick_labels || has_scale_type || has_primary_custom_range || (has_secondary_y_axis && secondary_scale_label)" class="w-40 cursor-pointer" @click="state.are_more_options_visible = !state.are_more_options_visible">
    <div class="flex items-center text-sm font-semibold text-primary-700">
      <IconHawkChevronRight v-if="!state.are_more_options_visible" class="w-6 h-6" />
      <IconHawkChevronDown v-else class="w-6 h-6" />
      {{ $t('More options') }}
    </div>
  </div>
  <div v-show="state.are_more_options_visible" class="col-span-12 flex flex-col gap-y-5 -mt-3">
    <template v-if="has_tick_labels">
      <div class="text-sm font-semibold text-gray-900 -mb-3">
        {{ $t('Tick labels') }}
      </div>
      <div class="flex gap-3">
        <SelectElement
          name="category_tick_label"
          :label="getAxis('category') === 'x' ? $t('X-axis') : $t('Y-axis')"
          :items="tick_label_types"
          :native="false"
          :can-clear="false"
          :can-deselect="false"
          default="show"
          class="w-full"
        />
        <SelectElement
          name="value_tick_label"
          :label="getAxis('value') === 'x' ? $t('X-axis') : $t('Y-axis')"
          :items="tick_label_types"
          :native="false"
          :can-clear="false"
          :can-deselect="false"
          default="show"
          class="w-full"
        />
      </div>
      <SelectElement
        v-if="has_secondary_y_axis"
        name="secondary_value_tick_label"
        :label="$t('Secondary Y-axis')"
        :items="tick_label_types"
        :native="false"
        :can-clear="false"
        :can-deselect="false"
        default="show"
        class="w-[calc(50%-6px)] -mt-3"
      />
    </template>
    <template v-if="has_scale_type">
      <div class="text-sm font-semibold text-gray-900 -mb-3">
        {{ $t('Scale type') }}
      </div>
      <div class="flex gap-3">
        <SelectElement
          name="primary_scale"
          :label="primary_scale_label"
          :items="scale_types"
          :native="false"
          :can-clear="false"
          :can-deselect="false"
          default="linear"
          class="w-full"
        />
        <SelectElement
          v-if="secondary_scale_label"
          name="secondary_scale"
          :label="secondary_scale_label"
          :items="scale_types"
          :native="false"
          :can-clear="false"
          :can-deselect="false"
          default="linear"
          class="w-full"
        />
      </div>
    </template>
    <template v-if="has_primary_custom_range">
      <div class="text-sm font-semibold text-gray-900 -mb-3">
        {{ primary_scale_label }} {{ $t('custom range') }}
      </div>
      <div class="flex gap-3">
        <TextElement
          name="custom_range_min"
          :label="$t('Minimum')"
          input-type="number"
          class="w-full"
        />
        <TextElement
          name="custom_range_max"
          :label="$t('Maximum')"
          input-type="number"
          class="w-full"
        />
      </div>
    </template>
    <template v-if="has_secondary_y_axis && secondary_scale_label">
      <div class="text-sm font-semibold text-gray-900 -mb-3">
        {{ secondary_scale_label }} {{ $t('custom range') }}
      </div>
      <div class="flex gap-3">
        <TextElement
          name="secondary_value_min"
          :label="$t('Minimum')"
          input-type="number"
          class="w-full"
        />
        <TextElement
          name="secondary_value_max"
          :label="$t('Maximum')"
          input-type="number"
          class="w-full"
        />
      </div>
    </template>
  </div>
</template>
